import React, { useState } from 'react'
import { useCreditCardFees } from '../../hooks/useCreditCardFess'
import { IFormData } from '../../types/app-context'
import { FormErrors } from '../../types/payment'
import {
  formatCardExpiry,
  formatCardNumber,
  getCardBrand,
} from '../../utils/formatting'
import FormField from '../FormField'

interface CreditCardPaymentProps {
  formData: IFormData
  errors: FormErrors
  onInputChange: (field: string, value: string) => void
  paymentValue: number
}

const CreditCardPayment: React.FC<CreditCardPaymentProps> = ({
  formData,
  errors,
  onInputChange,
  paymentValue,
}) => {
  const [cardBrand, setCardBrand] = useState('generic')
  const { installmentOptions, isLoading: isLoadingInstallments } =
    useCreditCardFees({ value: paymentValue })

  const handleCardNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardNumber(e.target.value)
    onInputChange('card-number', formatted)
    setCardBrand(getCardBrand(formatted))
  }

  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCardExpiry(e.target.value)
    onInputChange('card-expiry', formatted)
  }

  const handleCvvChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/\D/g, '')
    onInputChange('card-cvv', numericValue)
  }

  const getCardIcon = () => {
    switch (cardBrand) {
      case 'visa':
        return 'fab fa-cc-visa'
      case 'mastercard':
        return 'fab fa-cc-mastercard'
      case 'amex':
        return 'fab fa-cc-amex'
      default:
        return 'far fa-credit-card'
    }
  }

  return (
    <>
      <FormField
        label="Número do Cartão"
        required
        error={errors['card-number']}
      >
        <div className="input-wrapper">
          <input
            type="text"
            id="card-number"
            value={formData['card-number'] || ''}
            onChange={handleCardNumberChange}
            placeholder="0000 0000 0000 0000"
            maxLength={19}
            className={errors['card-number'] ? 'invalid' : ''}
          />
          <i className={`card-icon ${getCardIcon()}`}></i>
        </div>
      </FormField>

      <div className="input-group">
        <FormField
          label="Validade (MM/AA)"
          required
          error={errors['card-expiry']}
        >
          <input
            type="text"
            id="card-expiry"
            value={formData['card-expiry'] || ''}
            onChange={handleExpiryChange}
            placeholder="MM/AA"
            maxLength={5}
            className={errors['card-expiry'] ? 'invalid' : ''}
          />
        </FormField>

        <FormField label="CVV" required error={errors['card-cvv']}>
          <input
            type="text"
            id="card-cvv"
            value={formData['card-cvv'] || ''}
            onChange={handleCvvChange}
            placeholder="123"
            maxLength={4}
            className={errors['card-cvv'] ? 'invalid' : ''}
          />
        </FormField>
      </div>

      <FormField label="Nome no Cartão" required error={errors['card-holder']}>
        <input
          type="text"
          id="card-holder"
          value={formData['card-holder'] || ''}
          onChange={(e) =>
            onInputChange('card-holder', e.target.value.toUpperCase())
          }
          placeholder="NOME COMO ESTÁ NO CARTÃO"
          className={errors['card-holder'] ? 'invalid' : ''}
        />
      </FormField>

      <FormField label="Parcelas" required error={errors['card-installments']}>
        {isLoadingInstallments ? (
          <div
            style={{
              padding: '8px 10px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              color: '#666',
            }}
          >
            <i className="fas fa-spinner fa-spin"></i> Carregando opções de
            parcelamento...
          </div>
        ) : (
          <select
            id="card-installments"
            value={formData['card-installments'] || '1'}
            onChange={(e) => onInputChange('card-installments', e.target.value)}
            className={errors['card-installments'] ? 'invalid' : ''}
          >
            {installmentOptions.length > 0 ? (
              installmentOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.text}
                </option>
              ))
            ) : (
              <option value="1">1x sem juros</option>
            )}
          </select>
        )}
      </FormField>
    </>
  )
}

export default CreditCardPayment
